import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NaturalLanguageTaskInput = ({ onTaskParsed, onClose }) => {
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [aiVoice, setAiVoice] = useState(null);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [parsedTaskData, setParsedTaskData] = useState(null);

  // Initialize AI voice on component mount
  useEffect(() => {
    const initializeVoice = () => {
      if ('speechSynthesis' in window) {
        const voices = speechSynthesis.getVoices();
        // Find a good female voice
        const femaleVoice = voices.find(voice => 
          voice.name.toLowerCase().includes('female') || 
          voice.name.toLowerCase().includes('woman') ||
          voice.name.toLowerCase().includes('samantha') ||
          voice.name.toLowerCase().includes('karen') ||
          voice.name.toLowerCase().includes('moira') ||
          voice.name.toLowerCase().includes('alex') ||
          (voice.lang.startsWith('en') && voice.name.toLowerCase().includes('english'))
        ) || voices.find(voice => voice.lang.startsWith('en')) || voices[0];
        
        setAiVoice(femaleVoice);
      }
    };

    // Initialize immediately if voices are already loaded
    initializeVoice();
    
    // Also listen for voices to load (some browsers load them asynchronously)
    speechSynthesis.addEventListener('voiceschanged', initializeVoice);
    
    return () => {
      speechSynthesis.removeEventListener('voiceschanged', initializeVoice);
    };
  }, []);

  // AI Voice function
  const speakText = (text) => {
    if ('speechSynthesis' in window && aiVoice) {
      // Stop any current speech
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = aiVoice;
      utterance.rate = 0.9; // Slightly slower for clarity
      utterance.pitch = 1.1; // Slightly higher pitch
      utterance.volume = 0.8;
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      
      speechSynthesis.speak(utterance);
    }
  };

  const handleAIParsing = async () => {
    if (!input.trim()) {
      speakText("Please enter a task description first.");
      return;
    }

    setIsProcessing(true);

    try {
      speakText("Processing your task description...");
      
      const response = await axios.post(`${API}/ai/parse-task`, {
        user_input: input,
        context: {
          current_date: new Date().toISOString().split('T')[0],
          current_time: new Date().toTimeString().split(' ')[0].substring(0, 5)
        }
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const aiData = response.data.data;
        
        // Check if AI needs clarification
        if (aiData.missing && aiData.missing.length > 0) {
          speakText(`I need more information about: ${aiData.missing.join(', ')}`);
          return;
        }

        // Store parsed data and speak back the details
        setParsedTaskData(aiData.fields);
        setAwaitingConfirmation(true);
        
        // Create voice summary of extracted details
        const fields = aiData.fields;
        let voiceSummary = "Here's what I understood: ";
        
        if (fields.title) voiceSummary += `Title: ${fields.title}. `;
        if (fields.due_date) {
          const date = new Date(fields.due_date);
          const dateStr = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
          voiceSummary += `Date: ${dateStr}. `;
        }
        if (fields.due_time) voiceSummary += `Time: ${fields.due_time}. `;
        if (fields.priority) voiceSummary += `Priority: ${fields.priority}. `;
        if (fields.location) voiceSummary += `Location: ${fields.location}. `;
        if (fields.notes) voiceSummary += `Notes: ${fields.notes}. `;
        
        voiceSummary += "Should I create this task?";
        
        speakText(voiceSummary);
        
      } else {
        speakText("I couldn't understand your task description. Please try describing it differently.");
      }
    } catch (error) {
      console.error('AI parsing error:', error);
      speakText("I'm having trouble processing that right now. Please try again in a moment.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmTask = () => {
    if (parsedTaskData) {
      speakText("Perfect! Creating your task now.");
      onTaskParsed(parsedTaskData);
      onClose();
    }
  };

  const handleRejectTask = () => {
    speakText("No problem. Please describe your task again.");
    setAwaitingConfirmation(false);
    setParsedTaskData(null);
    setInput('');
  };

  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      speakText('Voice input is not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      speakText('Listening... please describe your task now.');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setIsListening(false);
      speakText(`I heard: ${transcript}. Let me process this for you.`);
      // Auto-process after voice input
      setTimeout(() => {
        handleAIParsing();
      }, 2000);
    };

    recognition.onerror = (event) => {
      speakText(`Voice recognition error: ${event.error}`);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg max-w-md mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">🤖 AI Task Creator</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {!awaitingConfirmation && (
        <>
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">
              Describe your task and I'll speak back the details for confirmation.
            </p>
            
            <div className="relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Example: Call Sarah tomorrow at 2pm about the project review"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows="4"
                disabled={isProcessing || isListening}
              />
              
              {/* Voice Input Button */}
              <button
                onClick={startVoiceInput}
                disabled={isProcessing || isListening}
                className={`absolute bottom-2 right-2 p-2 rounded-full transition-colors ${
                  isListening 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                title={isListening ? 'Listening...' : 'Voice input'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>
            </div>

            {(isListening || isSpeaking) && (
              <div className="mt-2 flex items-center space-x-2 text-blue-600">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-sm">
                  {isListening ? 'Listening... Speak now' : 'AI is speaking...'}
                </span>
              </div>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isProcessing}
            >
              Cancel
            </button>
            
            <button
              onClick={handleAIParsing}
              disabled={isProcessing || !input.trim() || isListening}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                isProcessing || !input.trim() || isListening
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isProcessing && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{isProcessing ? 'Processing...' : '🤖 Process with AI'}</span>
            </button>
          </div>
        </>
      )}

      {awaitingConfirmation && (
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">Task Details Confirmed</h4>
            <p className="text-sm text-gray-600">I've spoken the details back to you. Should I create this task?</p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleRejectTask}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ❌ No, Try Again
            </button>
            
            <button
              onClick={handleConfirmTask}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              ✅ Yes, Create Task
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          💡 Try: "Meeting with John next Friday at 10am", "High priority: finish report by tomorrow"
        </p>
      </div>
    </div>
  );
};

export default NaturalLanguageTaskInput;